import { TRoadblockCaseData, TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import { allDocumentSlotsReceived, anyDocSlotExists, probateChecklistItems } from "./ProbateItems";
import { TCasesDocumentType } from "../../models/CaseDocumentType1";
import { TRoadblockEvidenceFact, TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";
import { fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { Address3_displayString } from "../../codecs/Address";
import { TDocumentsInTitlesObtainedStatus } from "../../codecs/TitleCheck";

const contractsLink: TRoadblockEvidenceLink = { block: "case_contracts", section: null };
const epcLink: TRoadblockEvidenceLink = { block: "case_details", section: "epc" };
const chargesLink: TRoadblockEvidenceLink = { block: "charges_and_restrictions", section: null };
const managementCompanyLink: TRoadblockEvidenceLink = { block: "case_details", section: "management_company_details" };
const titleChecksLink: TRoadblockEvidenceLink = { block: "case_title_checks", section: null };

const propertyLabel = (p: { title_number: string } & Parameters<typeof Address3_displayString>[0]): string => {
    const address = Address3_displayString(p).trim();
    return address.length > 0 ? address : (p.title_number.trim() || "Property");
};

// One fact per document slot of the given types, surfacing each slot's received date.
const documentSlotsEvidence = (
    c: TRoadblockCaseData,
    types: Array<TCasesDocumentType>,
    label: string,
    link: TRoadblockEvidenceLink | null = null,
): Array<TRoadblockEvidenceFact> => {
    const slots = c.cases_documents.filter((d) => types.includes(d.type));
    if (slots.length === 0) {
        return [fact(label, "No document slot", false, link)];
    }
    return slots.map((slot, index) =>
        fact(
            slots.length > 1 ? `${label} ${index + 1}` : label,
            slot.received_date !== null ? `Received ${formatEvidenceDate(slot.received_date)}` : "Not received",
            slot.received_date !== null,
            link,
        ),
    );
};

// The case's EPC obligation is discharged. Deliberately broader than
// cases_epc.status, which only answers "what did our government lookup find?" —
// an EPC that arrived any other way (emailed in by the agent and filed by hand,
// attached by a case handler from the search list) discharges the obligation
// just as well, and cases_epc never learns about it.
//
// Reading only the status is what left a case showing "EPC Received — 1 missing"
// with the certificate visibly sitting in its Documents tab, and pushed handlers
// into clearing it with "Mark as not required" — a permanent false assertion
// that the property needs no EPC.
const epcIsSettled = (c: TRoadblockCaseData): boolean =>
    (c.epc !== null && (c.epc.status === "valid" || c.epc.status === "not_required"))
    || allDocumentSlotsReceived(c, ["epc"]);

// Documents referred to in a title but never lodged at HMLR cannot be obtained by
// anyone, so "some missing" settles the item rather than opening work nobody can finish
// — but it stays neutral in the evidence the authoriser signs off, never a green tick.
const documentsReferredToInTitleOutcome: Record<TDocumentsInTitlesObtainedStatus, { value: string, settled: boolean, status: TRoadblockEvidenceFact["status"] }> = {
    yes: { value: "All obtained", settled: true, status: "present" },
    some_missing: { value: "Some unobtainable", settled: true, status: "neutral" },
    no: { value: "Outstanding", settled: false, status: "missing" },
    unknown: { value: "Not checked", settled: false, status: "missing" },
};

const anyLeaseholdProperty = (c: TRoadblockCaseData) =>
    c.cases_properties.filter((p) => p.tenure === "leasehold").length > 0;

const anyFreeholdProperty = (c: TRoadblockCaseData) =>
    c.cases_properties.filter((p) => p.tenure === "freehold").length > 0;

const anyUnregisteredProperty = (c: TRoadblockCaseData) =>
    c.cases_properties.filter((p) => p.is_registered === false).length > 0;

// Either document discharges the obligation: the deeds bundle itself, or the
// epitome that abstracts it. Firms send one or the other, and the 13 live
// unregistered sales that already held deeds had them filed as an epitome.
const unregisteredTitleDocTypes: Array<TCasesDocumentType> = ["unregistered_deeds", "epitome_of_title"];

const unregisteredTitleSlotLabel = (type: TCasesDocumentType): string =>
    type === "epitome_of_title" ? "Epitome of title" : "Unregistered deeds";

const unregisteredTitleSlotsEvidence = (c: TRoadblockCaseData): Array<TRoadblockEvidenceFact> => {
    const typesPresent = unregisteredTitleDocTypes.filter((type) => anyDocSlotExists(c, [type]));
    return typesPresent.length === 0
        ? [fact("Unregistered deeds or epitome of title", "No document slot", false)]
        : typesPresent.flatMap((type) => documentSlotsEvidence(c, [type], unregisteredTitleSlotLabel(type)));
};

const anyFullTitleProperty = (c: TRoadblockCaseData) =>
    c.cases_properties.filter((p) => p.title_guarantee === "full").length > 0;

const anyFullTitleLeaseholdProperty = (c: TRoadblockCaseData) =>
    c.cases_properties.filter((p) => p.title_guarantee === "full" && p.tenure === "leasehold").length > 0;

// An LPE1 is served on the landlord or managing agent, so the obligation only
// exists when there is one to serve it on. A seller who owns the freehold
// reversion of their own leasehold title has nobody to ask, and a leasehold
// title alone used to open an item they could never resolve.
//
// `sl_order_lpe1` in GetJobsController already gates the job this way; this
// keeps the roadblock from contradicting the job list.
const lpe1Applies = (c: TRoadblockCaseData) =>
    anyLeaseholdProperty(c)
    && (c.case.has_management_company === true || c.case.lpe1_ordered !== null);

// cases.lpe1_ordered stopped being written when the management-companies tables
// landed (Jun 2026) — the order date now lives on each cases__management_companies
// link. Cases instructed before that only carry the old column, so both have to
// count as "ordered" or their receipt items get deleted out from under live work.
const lpe1Ordered = (c: TRoadblockCaseData) =>
    c.case.lpe1_ordered !== null
    || (
        c.management_companies.length > 0
        && c.management_companies.every((m) => m.pack_ordered !== null)
    );

/**
 * ContractReady drives collection of the Contract Ready Pack (the bundle that goes
 * out before exchange). Each item maps to a document or milestone gathered from
 * the existing cases / cases_documents / cases_properties data, so resolution is
 * fully predicate-driven — no bespoke UI required.
 *
 * Items intentionally NOT included here:
 *   - draft_contract / draft_tr1 generation and sending — Contracts roadblock
 *   - contract_details_added, os_conveyancer_details, os_client_details — Contracts roadblock
 *   - property_details_added — Title Checks roadblock (proves property-details
 *     completeness for every transaction type, not just sales)
 *
 * Items intentionally deferred (no reliable signal yet):
 *   - name-differs / marriage cert / statement of truth (no deterministic field)
 *   - LPA / lost capacity (deferred per grill — needs better detection)
 *   - planning permissions / building regs (no signal for "if we did the work")
 */
export const ContractReady: TRoadblockConfiguration = {
    type: "contract_ready",
    display_name: "Contract Ready Pack",
    only_create_if_resolved: [],
    requires_authoriser_signoff: false,
    blocks_exchange: true,
    create: {
        create_condition_description: "Sale cases, after instruction",
        predicate: (c) =>
            c.case.transaction_type === "sale"
            && c.case.is_instructed !== null
    },
    checklist: [
        {
            type: "all_documents_referred_to_in_titles_obtained",
            is_resolved_by_short_description: "All documents referred to in titles and deeds obtained",
            create: {
                create_condition_description: "Cases with at least one property",
                predicate: (c) => c.cases_properties.length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every property on the case having its "All documents referred to in titles and deeds obtained" title-check answer set to Yes or to Some missing. Some missing is a settled answer — those documents were never lodged at HMLR and cannot be obtained, so the contract pack is built around them.`,
                predicate: (c) =>
                    c.cases_properties.length > 0
                    && c.cases_properties.every((p) => documentsReferredToInTitleOutcome[p.all_documents_referred_to_in_titles_and_deeds_obtained].settled),
                evidence: (c) => c.cases_properties.map((p) => {
                    const outcome = documentsReferredToInTitleOutcome[p.all_documents_referred_to_in_titles_and_deeds_obtained];
                    return outcome.status === "neutral"
                        ? neutralFact(propertyLabel(p), outcome.value, titleChecksLink)
                        : fact(propertyLabel(p), outcome.value, outcome.status === "present", titleChecksLink);
                }),
            },
        },
        {
            type: "unregistered_deeds_obtained",
            is_resolved_by_short_description: "Obtain deeds or epitome of title (unregistered title)",
            create: {
                create_condition_description: "Cases with at least one title answered as unregistered",
                // Only an explicit No counts. is_registered starts null, so treating
                // "not answered yet" as unregistered would open a deeds item on every
                // freshly instructed sale.
                predicate: (c) => anyUnregisteredProperty(c),
            },
            resolve: {
                is_resolved_by_long_description: `Every unregistered deeds or epitome of title document slot on the case being uploaded and marked as received. An unregistered title is deduced from the deeds themselves, so there is nothing to build the contract pack from until they are in hand — add an "Unregistered deeds" document (or an "Epitome of Title", if that is what the other side sent) and mark it received once it arrives.`,
                predicate: (c) => allDocumentSlotsReceived(c, unregisteredTitleDocTypes),
                evidence: (c) => [
                    ...c.cases_properties
                        .filter((p) => p.is_registered === false)
                        .map((p) => neutralFact(propertyLabel(p), "Unregistered title", titleChecksLink)),
                    ...unregisteredTitleSlotsEvidence(c),
                ],
            },
        },
        {
            type: "epc_received",
            is_resolved_by_short_description: "EPC Received",
            create: {
                create_condition_description: "All cases",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: `The case holding an EPC, or not needing one. Resolves when a valid (non-expired) certificate has been found and attached, when the certificate has been filed into the case's EPC document slot by any route, or when the case handler has marked the property as not requiring an EPC. Stays open while the EPC is still pending, expired, or genuinely absent.`,
                predicate: epcIsSettled,
                evidence: (c) => {
                    if (c.epc === null) {
                        return documentSlotsEvidence(c, ["epc"], "EPC", epcLink);
                    }
                    const value =
                        c.epc.status === "valid" ? "Valid certificate attached"
                        : c.epc.status === "not_required" ? "Not required"
                        // The lookup didn't place this one, so say where it did come from
                        // rather than reporting the stale lookup verdict over the top of a
                        // certificate the case demonstrably holds.
                        : allDocumentSlotsReceived(c, ["epc"]) ? "Certificate on file"
                        : c.epc.status === "expired" ? "Certificate expired"
                        : c.epc.status === "none_found" ? "None found"
                        : "No automatic match";
                    return [fact("EPC", value, epcIsSettled(c), epcLink)];
                },
            },
        },
        {
            type: "arrange_new_epc",
            is_resolved_by_short_description: "Arrange a new EPC",
            create: {
                create_condition_description: "Cases where no valid EPC exists and we agreed to arrange a new one",
                // The settled check is load-bearing: without it a case whose certificate was
                // filed by hand resolves "EPC Received" and simultaneously opens "Arrange a
                // new EPC" for a property that already has one.
                predicate: (c) =>
                    c.epc !== null
                    && (c.epc.status === "expired" || c.epc.status === "none_found")
                    && c.epc.who_arranges_new_epc === "us"
                    && !epcIsSettled(c),
            },
            resolve: {
                is_resolved_by_long_description: `Marking "we've sorted a new EPC" on the case's EPC section once a replacement assessment has been arranged. The fresh certificate later flips the EPC status back to valid.`,
                predicate: (c) => c.epc !== null && c.epc.new_epc_sorted === true,
                evidence: (c) => [
                    fact(
                        "New EPC arranged",
                        c.epc !== null && c.epc.new_epc_sorted === true ? "Sorted" : "Not yet sorted",
                        c.epc !== null && c.epc.new_epc_sorted === true,
                        epcLink,
                    ),
                ],
            },
        },
        {
            type: "lpe1_ordered",
            is_resolved_by_short_description: "LPE1 ordered (leasehold)",
            create: {
                create_condition_description: "Cases with at least one leasehold title and a management company, until the LPE1 is ordered",
                predicate: (c) => lpe1Applies(c),
            },
            resolve: {
                is_resolved_by_long_description: `Marking the pack as ordered on every management company linked to the case, which records the order date against each one.`,
                predicate: (c) => lpe1Ordered(c),
                evidence: (c) => {
                    if (c.management_companies.length === 0) {
                        return c.case.lpe1_ordered !== null
                            ? [fact("LPE1 ordered", `Ordered ${formatEvidenceDate(c.case.lpe1_ordered)}`, true, contractsLink)]
                            : [fact("Management companies", "None linked", false, managementCompanyLink)];
                    }
                    return c.management_companies.map((m) =>
                        fact(
                            m.company_name.trim() || "Management company",
                            m.pack_ordered !== null ? `Ordered ${formatEvidenceDate(m.pack_ordered)}` : "Not ordered",
                            m.pack_ordered !== null,
                            managementCompanyLink,
                        )
                    );
                },
            },
        },
        {
            type: "lpe1_received_for_all_leasehold_titles",
            is_resolved_by_short_description: "LPE1 received (leasehold)",
            create: {
                // Sequenced pair: the receipt half only appears once the LPE1 has been
                // ordered, so one row shows at a time — "Order LPE1", then "LPE1
                // received" — rather than two competing rows for the same real thing.
                create_condition_description: "Cases with at least one leasehold title and a management company, where the LPE1 has been ordered",
                predicate: (c) => lpe1Applies(c) && lpe1Ordered(c),
            },
            resolve: {
                is_resolved_by_long_description: `Every LPE1 document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["lpe1"]),
                evidence: (c) => documentSlotsEvidence(c, ["lpe1"], "LPE1", contractsLink),
            },
        },
        {
            type: "fme1_received",
            is_resolved_by_short_description: "FME1 received",
            create: {
                create_condition_description: "Freehold properties on a case where a management company exists",
                predicate: (c) => c.case.has_management_company === true && anyFreeholdProperty(c),
            },
            resolve: {
                is_resolved_by_long_description: `Every FME1 document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["fme1"]),
                evidence: (c) => documentSlotsEvidence(c, ["fme1"], "FME1", contractsLink),
            },
        },
        {
            type: "ast_received",
            is_resolved_by_short_description: "AST or tenancy agreement received",
            create: {
                create_condition_description: "Cases sold subject to an AST",
                predicate: (c) => c.case.vacant_possession_or_ast === "ast",
            },
            resolve: {
                is_resolved_by_long_description: `Every AST and tenancy agreement document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["ast", "tenancy_agreement"]),
                evidence: (c) => documentSlotsEvidence(c, ["ast", "tenancy_agreement"], "AST / tenancy agreement", contractsLink),
            },
        },
        ...probateChecklistItems(),
        {
            // On the new internal PIF flow a completed BASPI supersedes TA6 + TA7
            // (leasehold) + TA10 (fittings) in a single document. Created for any
            // sale on the new flow, full OR limited title. Resolved by the
            // authoritative "all required signers signed" flag (the generated PIF
            // PDF is produced downstream of the flag and can fail without blocking
            // submit, so we key off the flag, not the document).
            type: "pif_received",
            is_resolved_by_short_description: "Property Information Form (BASPI) completed",
            create: {
                create_condition_description: "Sale cases on the new internal PIF flow",
                predicate: (c) => c.case.is_on_new_pif_flow,
            },
            resolve: {
                is_resolved_by_long_description: `The seller's Property Information Form (BASPI) being completed and signed by every required signer (cases.client_says_pifs_submitted is true). Supersedes the TA6/TA7/TA10 documents for cases on the new PIF flow.`,
                predicate: (c) => c.case.client_says_pifs_submitted,
                evidence: (c) => [
                    fact(
                        "Property Information Form (BASPI)",
                        c.case.client_says_pifs_submitted ? "Completed & signed" : "Not completed",
                        c.case.client_says_pifs_submitted,
                        contractsLink,
                    ),
                ],
            },
        },
        {
            type: "ta6_received",
            is_resolved_by_short_description: "TA6 received",
            create: {
                create_condition_description: "Legacy-flow cases with a full-title-guarantee property (not on the new PIF flow)",
                predicate: (c) => anyFullTitleProperty(c) && !c.case.is_on_new_pif_flow,
            },
            resolve: {
                is_resolved_by_long_description: `Every TA6 (Property Information Form) document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["ta6"]),
                evidence: (c) => documentSlotsEvidence(c, ["ta6"], "TA6", contractsLink),
            },
        },
        {
            type: "ta7_received",
            is_resolved_by_short_description: "TA7 received",
            create: {
                create_condition_description: "Legacy-flow cases with a full-title-guarantee leasehold property (not on the new PIF flow)",
                predicate: (c) => anyFullTitleLeaseholdProperty(c) && !c.case.is_on_new_pif_flow,
            },
            resolve: {
                is_resolved_by_long_description: `Every TA7 (Leasehold Information Form) document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["ta7"]),
                evidence: (c) => documentSlotsEvidence(c, ["ta7"], "TA7", contractsLink),
            },
        },
        {
            type: "ta10_received",
            is_resolved_by_short_description: "TA10 received",
            create: {
                create_condition_description: "Legacy-flow cases with a full-title-guarantee property (not on the new PIF flow)",
                predicate: (c) => anyFullTitleProperty(c) && !c.case.is_on_new_pif_flow,
            },
            resolve: {
                is_resolved_by_long_description: `Every TA10 (Fittings and Contents Form) document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["ta10"]),
                evidence: (c) => documentSlotsEvidence(c, ["ta10"], "TA10", contractsLink),
            },
        },
        {
            type: "local_authority_search_received",
            is_resolved_by_short_description: "Local authority search received",
            create: {
                create_condition_description: "Cases where searches have been ordered",
                predicate: (c) => c.case.searches_ordered !== null,
            },
            resolve: {
                is_resolved_by_long_description: `Every local authority search document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["local_authority_search"]),
                evidence: (c) => documentSlotsEvidence(c, ["local_authority_search"], "Local authority search"),
            },
        },
        {
            type: "environment_search_received",
            is_resolved_by_short_description: "Environment search received",
            create: {
                create_condition_description: "Cases where searches have been ordered",
                predicate: (c) => c.case.searches_ordered !== null,
            },
            resolve: {
                is_resolved_by_long_description: `Every environment search document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["environment_search"]),
                evidence: (c) => documentSlotsEvidence(c, ["environment_search"], "Environment search"),
            },
        },
        {
            type: "drainage_search_received",
            is_resolved_by_short_description: "Drainage search received",
            create: {
                create_condition_description: "Cases where searches have been ordered",
                predicate: (c) => c.case.searches_ordered !== null,
            },
            resolve: {
                is_resolved_by_long_description: `Every drainage search document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["drainage_search"]),
                evidence: (c) => documentSlotsEvidence(c, ["drainage_search"], "Drainage search"),
            },
        },
        {
            type: "management_company_details_obtained",
            is_resolved_by_short_description: "Management company details obtained",
            create: {
                create_condition_description: "Cases with a management company",
                predicate: (c) => c.case.has_management_company === true,
            },
            resolve: {
                is_resolved_by_long_description: `Linking at least one management company to the case, each with a name and at least one contact channel (email or phone).`,
                predicate: (c) =>
                    c.management_companies.length > 0
                    && c.management_companies.every((m) =>
                        m.company_name.trim() !== ""
                        && (
                            (m.company_email !== null && m.company_email.trim() !== "")
                            || (m.company_phone_number !== null && m.company_phone_number.trim() !== "")
                        )
                    ),
                evidence: (c) =>
                    c.management_companies.length === 0
                        ? [fact("Management companies", "None linked", false, managementCompanyLink)]
                        : c.management_companies.map((m) => {
                            const email = (m.company_email || "").trim();
                            const phone = (m.company_phone_number || "").trim();
                            const hasContact = email !== "" || phone !== "";
                            const hasName = m.company_name.trim() !== "";
                            return fact(
                                hasName ? m.company_name : "Management company",
                                hasName && hasContact
                                    ? (email !== "" ? email : phone)
                                    : "Missing contact",
                                hasName && hasContact,
                                managementCompanyLink,
                            );
                        }),
            },
        },
        {
            type: "account_numbers_received_for_all_charges",
            is_resolved_by_short_description: "Account numbers received for all charges",
            create: {
                create_condition_description: "Cases with at least one charge",
                predicate: (c) => c.cases_charges.length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every charge on the case having an account number set.`,
                predicate: (c) =>
                    c.cases_charges.length > 0
                    && c.cases_charges.every((ch) => ch.account_number.trim() !== ""),
                evidence: (c) =>
                    c.cases_charges.length === 0
                        ? [neutralFact("Charges", "No charges on case", chargesLink)]
                        : c.cases_charges.map((ch, index) => {
                            const accountNumber = ch.account_number.trim();
                            return fact(
                                `Charge ${index + 1} account number`,
                                accountNumber !== "" ? accountNumber : "Not set",
                                accountNumber !== "",
                                chargesLink,
                            );
                        }),
            },
        },
        {
            type: "auction_pack_sent",
            is_resolved_by_short_description: "Auction pack sent",
            create: {
                create_condition_description: "Auction sales only",
                predicate: (c) => c.case.auction_status === "yes",
            },
            resolve: {
                is_resolved_by_long_description: `Marking the auction pack as sent in the Contracts tab's Auction section, once the pack has gone out to the auctioneer. On a case that came to us through ConveyNext this also reports the milestone back to the introducer, so record it when the pack actually goes rather than in advance.`,
                predicate: (c) => c.case.auction_pack_sent !== null,
                evidence: (c) => [
                    fact(
                        "Auction pack",
                        c.case.auction_pack_sent !== null ? `Sent ${formatEvidenceDate(c.case.auction_pack_sent)}` : "Not sent",
                        c.case.auction_pack_sent !== null,
                        contractsLink,
                    ),
                ],
            },
        },
        {
            type: "first_redemption_statement_received",
            is_resolved_by_short_description: "First redemption statement received for all charges",
            create: {
                create_condition_description: "Cases with at least one charge",
                predicate: (c) => c.cases_charges.length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every charge on the case having a redemption statement received against it.`,
                predicate: (c) =>
                    c.cases_charges.length > 0
                    && c.cases_charges.every((ch) => ch.has_redemption_statement),
                evidence: (c) =>
                    c.cases_charges.length === 0
                        ? [neutralFact("Charges", "No charges on case", chargesLink)]
                        : c.cases_charges.map((ch, index) =>
                            fact(
                                `Charge ${index + 1} redemption statement`,
                                ch.has_redemption_statement ? "Received" : "Not received",
                                ch.has_redemption_statement,
                                chargesLink,
                            ),
                        ),
            },
        },
    ],
};
